import { useEffect, useRef } from 'react';

import { Stack, Typography } from '@mui/material';

import { tokens } from '../../../../../../theme/generated/tokens';
import { DraggableRows } from './_components/DraggableRows';
import { SettingsRow } from './_components/SettingsRow';
import { useSettingsDrawerState } from './_hooks/useSettingsDrawerState';
import { SettingsDrawerContext } from './context';
import type { SettingsRowModel } from './types';
import { WithRef } from '../../../../../../types';
import { Button } from '../../../../../buttons';
import {
  CloseDrawerButton,
  Drawer,
  DrawerProps,
  useDrawer,
} from '../../../../../drawer';
import { ArrowCounterClockwise } from '../../../../../icons';
import { useTableContext } from '../../../../hooks';

const SettingsDrawer = ({
  ref,
  ...props
}: WithRef<HTMLDivElement, DrawerProps>) => {
  const {
    api: {
      current: {
        state: { columnSettings },
        restoreDefaults,
        columnsInfo: { groupedColumnsProps },
      },
    },
  } = useTableContext()!;

  const lockedGroupRef = useRef<HTMLDivElement | null>(null);
  const unlockedGroupRef = useRef<HTMLDivElement | null>(null);

  const settingsDrawerState = useSettingsDrawerState(!!props.open);
  const { frozenColumns, lockedColumns, unlockedColumns } = settingsDrawerState;

  const visibleFrozenColumns = frozenColumns.filter(
    (column: SettingsRowModel) => !column.excludeFromSettings
  );
  const visibleLockedColumns = lockedColumns.filter(
    (column: SettingsRowModel) => !column.excludeFromSettings
  );
  const visibleUnlockedColumns = unlockedColumns.filter(
    (column: SettingsRowModel) => !column.excludeFromSettings
  );

  useEffect(() => {
    if (!settingsDrawerState.initialized) {
      settingsDrawerState.initialize({
        groupedColumnsProps,
        settings: columnSettings,
      });
    }
  }, [groupedColumnsProps, settingsDrawerState, columnSettings]);

  return (
    <Drawer ref={ref} size="small" testId="table-settings-drawer" {...props}>
      <SettingsDrawerContext.Provider
        value={{
          ...settingsDrawerState,
          lockedGroupRef,
          unlockedGroupRef,
        }}
      >
        <CloseDrawerButton />
        <Button
          size="sm"
          variant="ghost"
          startIcon={<ArrowCounterClockwise />}
          onClick={() => {
            restoreDefaults();
            settingsDrawerState.restoreDefaults();
          }}
          sx={{ position: 'absolute', top: 24, right: 32 }}
        >
          Restore default
        </Button>
        <Stack pt={68}>
          <Typography variant="h5" mb={24} pl={32}>
            Table management
          </Typography>

          {!!visibleFrozenColumns.length &&
            visibleFrozenColumns.map((col: SettingsRowModel) => (
              <SettingsRow key={col.id} column={col} />
            ))}
          <DraggableRows
            rows={visibleLockedColumns}
            group="lockedColumns"
            sx={{
              borderBottom: visibleLockedColumns.length
                ? `1px solid ${tokens.theme.border.structural.neutral}`
                : undefined,
            }}
            ref={lockedGroupRef}
          />
          <DraggableRows
            rows={visibleUnlockedColumns}
            group="unlockedColumns"
            ref={unlockedGroupRef}
          />
        </Stack>
      </SettingsDrawerContext.Provider>
    </Drawer>
  );
};

SettingsDrawer.displayName = 'SettingsDrawer';

export const useSettingsDrawer = () => {
  const { open } = useDrawer();
  return {
    openSettingsDrawer: () => open({ component: SettingsDrawer, props: {} }),
  };
};
